// src/screens/ExpenseScreen.tsx - Android完全再現 + フィルタ拡張
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  IconButton,
  Fab,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button
} from '@mui/material';
import {
  Add,
  MoneyOff,
  Edit,
  Delete,
  CalendarToday,
  DateRange
} from '@mui/icons-material';
import { getAllExpenses, getAllDeliveries, deleteExpense } from '../services/dataService';
import { Expense, DeliveryRecord } from '../types';
import AddExpenseDialog from '../components/AddExpenseDialog';

interface Props {
  onShowSnackbar: (message: string, severity?: 'success' | 'error') => void;
}

function ExpenseScreen({ onShowSnackbar }: Props) {
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [deliveries, setDeliveries] = useState<DeliveryRecord[]>([]);
  const [selectedPeriod, setSelectedPeriod] = useState('month');
  const [selectedSpecificDate, setSelectedSpecificDate] = useState<Date | null>(null);
  const [selectedStartDate, setSelectedStartDate] = useState<Date | null>(null);
  const [selectedEndDate, setSelectedEndDate] = useState<Date | null>(null);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [showDateRangePicker, setShowDateRangePicker] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingExpense, setEditingExpense] = useState<Expense | undefined>();
  const [deleteDialog, setDeleteDialog] = useState<{ open: boolean; expense?: Expense }>({ open: false });

  const [calendarYM, setCalendarYM] = useState({ year: new Date().getFullYear(), month: new Date().getMonth() });
  const [calendarDate, setCalendarDate] = useState(new Date());
  
  const [rangeCalendarYM, setRangeCalendarYM] = useState({ year: new Date().getFullYear(), month: new Date().getMonth() });
  const [rangeStart, setRangeStart] = useState<Date | null>(null);
  const [rangeEnd, setRangeEnd] = useState<Date | null>(null);
  const [selectingStart, setSelectingStart] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [expensesData, deliveriesData] = await Promise.all([
        getAllExpenses(),
        getAllDeliveries()
      ]);
      setExpenses(expensesData);
      setDeliveries(deliveriesData);
    } catch (error) {
      onShowSnackbar('データの読み込みに失敗しました', 'error');
    } finally {
      setLoading(false);
    }
  };

  const getDateRange = () => {
    const today = new Date();
    const todayStr = today.toISOString().split('T')[0];

    if (selectedPeriod === 'today') {
      return { start: todayStr, end: todayStr };
    } else if (selectedPeriod === 'week') {
      const weekAgo = new Date(today);
      weekAgo.setDate(today.getDate() - 6);
      return { start: weekAgo.toISOString().split('T')[0], end: todayStr };
    } else if (selectedPeriod === 'month') {
      const monthStart = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-01`;
      return { start: monthStart, end: todayStr };
    } else if (selectedPeriod === 'lastMonth') {
      const lastMonth = today.getMonth() === 0 ? 11 : today.getMonth() - 1;
      const lastMonthYear = today.getMonth() === 0 ? today.getFullYear() - 1 : today.getFullYear();
      const monthStart = `${lastMonthYear}-${String(lastMonth + 1).padStart(2, '0')}-01`;
      const monthEnd = new Date(lastMonthYear, lastMonth + 1, 0);
      return { start: monthStart, end: monthEnd.toISOString().split('T')[0] };
    } else if (selectedPeriod === 'specific' && selectedSpecificDate) {
      const dateStr = selectedSpecificDate.toISOString().split('T')[0];
      return { start: dateStr, end: dateStr };
    } else if (selectedPeriod === 'range' && selectedStartDate && selectedEndDate) {
      return { 
        start: selectedStartDate.toISOString().split('T')[0], 
        end: selectedEndDate.toISOString().split('T')[0] 
      };
    } else if (selectedPeriod === 'all') {
      return { start: '2000-01-01', end: '2099-12-31' };
    }
    
    return { start: todayStr, end: todayStr };
  };

  const dateRange = getDateRange();
  const filteredExpenses = expenses.filter(e => e.date >= dateRange.start && e.date <= dateRange.end);
  const filteredDeliveries = deliveries.filter(d => d.date >= dateRange.start && d.date <= dateRange.end);

  const totalExpense = filteredExpenses.reduce((sum, e) => sum + e.amount, 0);
  const totalRevenue = filteredDeliveries.reduce((sum, d) => sum + d.totalRevenue, 0);
  const netProfit = totalRevenue - totalExpense;
  const profitRate = totalRevenue > 0 ? Math.round((netProfit / totalRevenue) * 100) : 0;

  const getCategoryStats = () => {
    const categoryMap = new Map<string, { amount: number; count: number }>();
    
    filteredExpenses.forEach(e => {
      const current = categoryMap.get(e.category) || { amount: 0, count: 0 };
      categoryMap.set(e.category, {
        amount: current.amount + e.amount,
        count: current.count + 1
      });
    });

    return Array.from(categoryMap.entries())
      .map(([category, data]) => ({ category, ...data }))
      .sort((a, b) => b.amount - a.amount);
  };

  const categoryStats = getCategoryStats();

  const prevMonth = () => {
    if (calendarYM.month === 0) {
      setCalendarYM({ year: calendarYM.year - 1, month: 11 });
    } else {
      setCalendarYM({ year: calendarYM.year, month: calendarYM.month - 1 });
    }
  };

  const nextMonth = () => {
    if (calendarYM.month === 11) {
      setCalendarYM({ year: calendarYM.year + 1, month: 0 });
    } else {
      setCalendarYM({ year: calendarYM.year, month: calendarYM.month + 1 });
    }
  };

  const rangePrevMonth = () => {
    if (rangeCalendarYM.month === 0) {
      setRangeCalendarYM({ year: rangeCalendarYM.year - 1, month: 11 });
    } else {
      setRangeCalendarYM({ year: rangeCalendarYM.year, month: rangeCalendarYM.month - 1 });
    }
  };

  const rangeNextMonth = () => {
    if (rangeCalendarYM.month === 11) {
      setRangeCalendarYM({ year: rangeCalendarYM.year + 1, month: 0 });
    } else {
      setRangeCalendarYM({ year: rangeCalendarYM.year, month: rangeCalendarYM.month + 1 });
    }
  };

  const selectDate = (day: number) => {
    const newDate = new Date(calendarYM.year, calendarYM.month, day);
    setCalendarDate(newDate);
  };

  const selectRangeDate = (day: number) => {
    const newDate = new Date(rangeCalendarYM.year, rangeCalendarYM.month, day);
    
    if (selectingStart) {
      setRangeStart(newDate);
      if (rangeEnd && newDate > rangeEnd) {
        setRangeEnd(null);
      }
      setSelectingStart(false);
    } else {
      if (rangeStart && newDate < rangeStart) {
        setRangeEnd(rangeStart);
        setRangeStart(newDate);
      } else {
        setRangeEnd(newDate);
      }
      setSelectingStart(true);
    }
  };

  const isSelected = (day: number) => {
    return (
      calendarDate.getDate() === day &&
      calendarDate.getMonth() === calendarYM.month &&
      calendarDate.getFullYear() === calendarYM.year
    );
  };

  const isToday = (day: number, ym: { year: number; month: number }) => {
    const today = new Date();
    return (
      today.getDate() === day &&
      today.getMonth() === ym.month &&
      today.getFullYear() === ym.year
    );
  };

  const isInRange = (day: number) => {
    if (!rangeStart || !rangeEnd) return false;
    const date = new Date(rangeCalendarYM.year, rangeCalendarYM.month, day);
    return date >= rangeStart && date <= rangeEnd;
  };

  const isRangeEnd = (day: number) => {
    const date = new Date(rangeCalendarYM.year, rangeCalendarYM.month, day);
    return (
      (rangeStart && date.getTime() === rangeStart.getTime()) ||
      (rangeEnd && date.getTime() === rangeEnd.getTime())
    );
  };

  const confirmDateSelection = () => {
    setSelectedSpecificDate(calendarDate);
    setSelectedPeriod('specific');
    setShowDatePicker(false);
  };

  const confirmRangeSelection = () => {
    if (rangeStart && rangeEnd) {
      setSelectedStartDate(rangeStart);
      setSelectedEndDate(rangeEnd);
      setSelectedPeriod('range');
      setShowDateRangePicker(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteDialog.expense?.id) return;

    try {
      await deleteExpense(deleteDialog.expense.id);
      await loadData();
      onShowSnackbar('支出を削除しました');
      setDeleteDialog({ open: false });
    } catch (error) {
      onShowSnackbar('削除に失敗しました', 'error');
    }
  };

  const handleEdit = (expense: Expense) => {
    setEditingExpense(expense);
    setShowAddDialog(true);
  };

  const formatCurrency = (amount: number) => `¥${amount.toLocaleString()}`;

  if (loading) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography color="white">読み込み中...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2, pb: 10 }}>
      <Card sx={{ mb: 1.5, bgcolor: '#1a1a1a', borderRadius: '16px' }}>
        <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Box>
              <Typography variant="h5" fontWeight="bold" color="white">
                支出管理
              </Typography>
              <Typography fontSize={16} color="#888">
                経費を記録して収支を管理
              </Typography>
            </Box>
            <MoneyOff sx={{ fontSize: 32, color: '#EF4444' }} />
          </Box>

          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {[
              { key: 'today', label: '今日' },
              { key: 'week', label: '今週' },
              { key: 'month', label: '今月' },
              { key: 'lastMonth', label: '先月' },
              { key: 'all', label: '全期間' }
            ].map(period => (
              <Chip
                key={period.key}
                label={period.label}
                onClick={() => setSelectedPeriod(period.key)}
                sx={{
                  bgcolor: selectedPeriod === period.key ? '#EF4444' : '#333',
                  color: selectedPeriod === period.key ? '#fff' : '#888',
                  fontWeight: selectedPeriod === period.key ? 'bold' : 'normal',
                  fontSize: 12,
                  '&:hover': {
                    bgcolor: selectedPeriod === period.key ? '#dc2626' : '#444'
                  }
                }}
              />
            ))}
          </Box>

          <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<CalendarToday sx={{ fontSize: 16 }} />}
              onClick={() => setShowDatePicker(true)}
              sx={{
                borderColor: selectedPeriod === 'specific' ? '#EF4444' : '#333',
                color: selectedPeriod === 'specific' ? '#EF4444' : '#888',
                fontSize: 13,
                py: 1,
                '&:hover': {
                  borderColor: selectedPeriod === 'specific' ? '#EF4444' : '#444',
                  bgcolor: 'transparent'
                }
              }}
            >
              {selectedPeriod === 'specific' && selectedSpecificDate
                ? selectedSpecificDate.toLocaleDateString('ja-JP', { month: 'numeric', day: 'numeric' })
                : '特定日'}
            </Button>

            <Button
              fullWidth
              variant="outlined"
              startIcon={<DateRange sx={{ fontSize: 16 }} />}
              onClick={() => setShowDateRangePicker(true)}
              sx={{
                borderColor: selectedPeriod === 'range' ? '#EF4444' : '#333',
                color: selectedPeriod === 'range' ? '#EF4444' : '#888',
                fontSize: 13,
                py: 1,
                '&:hover': {
                  borderColor: selectedPeriod === 'range' ? '#EF4444' : '#444',
                  bgcolor: 'transparent'
                }
              }}
            >
              {selectedPeriod === 'range' && selectedStartDate && selectedEndDate
                ? `${selectedStartDate.toLocaleDateString('ja-JP', { month: 'numeric', day: 'numeric' })}-${selectedEndDate.toLocaleDateString('ja-JP', { month: 'numeric', day: 'numeric' })}`
                : '期間指定'}
            </Button>
          </Box>
        </CardContent>
      </Card>

      <Card sx={{ mb: 1.5, bgcolor: netProfit >= 0 ? '#0a2318' : '#2a0a0a', borderRadius: '16px' }}>
        <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
          <Box sx={{ textAlign: 'center', mb: 2.5 }}>
            <Typography fontSize={16} color="#888" mb={1}>
              純利益
            </Typography>
            <Typography fontSize={36} fontWeight="bold" color={netProfit >= 0 ? '#10B981' : '#EF4444'}>
              {formatCurrency(netProfit)}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', gap: 1.5, mb: 1.5 }}>
            <Card sx={{ flex: 1, bgcolor: '#2a2a2a', borderRadius: '8px' }}>
              <CardContent sx={{ p: 1.5, textAlign: 'center', '&:last-child': { pb: 1.5 } }}>
                <Typography fontSize={17} fontWeight="bold" color="#10B981">
                  {formatCurrency(totalRevenue)}
                </Typography>
                <Typography fontSize={13} color="#888">収益</Typography>
              </CardContent>
            </Card>

            <Card sx={{ flex: 1, bgcolor: '#2a2a2a', borderRadius: '8px' }}>
              <CardContent sx={{ p: 1.5, textAlign: 'center', '&:last-child': { pb: 1.5 } }}>
                <Typography fontSize={17} fontWeight="bold" color="#EF4444">
                  {formatCurrency(totalExpense)}
                </Typography>
                <Typography fontSize={13} color="#888">支出</Typography>
              </CardContent>
            </Card>
          </Box>

          {totalRevenue > 0 && (
            <Typography fontSize={16} color={profitRate >= 50 ? '#10B981' : '#F59E0B'} fontWeight="medium">
              利益率: {profitRate}%
            </Typography>
          )}
        </CardContent>
      </Card>

      {categoryStats.length > 0 && (
        <Card sx={{ mb: 1.5, bgcolor: '#1a1a1a', borderRadius: '12px' }}>
          <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
            <Typography fontSize={17} fontWeight="bold" color="white" mb={2}>
              📊 カテゴリ別支出
            </Typography>
            {categoryStats.slice(0, 5).map((stat, index) => {
              const percentage = totalExpense > 0 ? Math.round((stat.amount / totalExpense) * 100) : 0;
              
              return (
                <Box key={index} sx={{ mb: index < Math.min(5, categoryStats.length) - 1 ? 2 : 0 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Box>
                      <Typography fontSize={16} fontWeight="medium" color="white">
                        {stat.category}
                      </Typography>
                      <Typography fontSize={13} color="#888">
                        {stat.count}回
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: 'right' }}>
                      <Typography fontSize={17} fontWeight="bold" color="#EF4444">
                        {formatCurrency(stat.amount)}
                      </Typography>
                      <Typography fontSize={13} color="#888">
                        {percentage}%
                      </Typography>
                    </Box>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={percentage}
                    sx={{
                      height: 6,
                      borderRadius: 3,
                      bgcolor: '#333',
                      '& .MuiLinearProgress-bar': {
                        bgcolor: '#EF4444',
                        borderRadius: 3
                      }
                    }}
                  />
                </Box>
              );
            })}
          </CardContent>
        </Card>
      )}

      <Card sx={{ mb: 1, bgcolor: '#1a1a1a', borderRadius: '12px' }}>
        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography fontSize={17} fontWeight="bold" color="white">
              📋 支出一覧
            </Typography>
            <Typography fontSize={16} color="#EF4444" fontWeight="medium">
              {filteredExpenses.length}件
            </Typography>
          </Box>
        </CardContent>
      </Card>

      {filteredExpenses.length === 0 ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 300, color: '#666' }}>
          <MoneyOff sx={{ fontSize: 64, mb: 2 }} />
          <Typography fontSize={17} fontWeight="medium">
            支出記録がありません
          </Typography>
          <Typography fontSize={15} color="#666">
            右下の+ボタンから追加できます
          </Typography>
        </Box>
      ) : (
        filteredExpenses.sort((a, b) => b.date.localeCompare(a.date)).map((expense, index) => (
          <Card key={expense.id || index} sx={{ mb: 1, bgcolor: '#2a2a2a', borderRadius: '8px' }}>
            <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box sx={{ flex: 1 }}>
                  <Typography fontSize={16} fontWeight="bold" color="white">
                    {expense.category}
                  </Typography>
                  {expense.description && (
                    <Typography fontSize={14} color="#888">
                      {expense.description}
                    </Typography>
                  )}
                  <Typography fontSize={13} color="#666">
                    {new Date(expense.date).toLocaleDateString('ja-JP', { month: 'numeric', day: 'numeric', weekday: 'short' })}
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography fontSize={18} fontWeight="bold" color="#EF4444" mr={1}>
                    {formatCurrency(expense.amount)}
                  </Typography>
                  <IconButton size="small" onClick={() => handleEdit(expense)} sx={{ color: '#888', p: 0.5 }}>
                    <Edit sx={{ fontSize: 16 }} />
                  </IconButton>
                  <IconButton size="small" onClick={() => setDeleteDialog({ open: true, expense })} sx={{ color: '#EF4444', p: 0.5 }}>
                    <Delete sx={{ fontSize: 16 }} />
                  </IconButton>
                </Box>
              </Box>
            </CardContent>
          </Card>
        ))
      )}

      <Fab
        onClick={() => {
          setEditingExpense(undefined);
          setShowAddDialog(true);
        }}
        sx={{
          position: 'fixed',
          bottom: 88,
          right: 24,
          width: 64,
          height: 64,
          bgcolor: '#EF4444',
          color: 'white',
          '&:hover': { bgcolor: '#dc2626' }
        }}
      >
        <Add sx={{ fontSize: 28 }} />
      </Fab>

      <AddExpenseDialog
        open={showAddDialog}
        onClose={() => {
          setShowAddDialog(false);
          setEditingExpense(undefined);
        }}
        onSuccess={() => {
          loadData();
          setShowAddDialog(false);
          setEditingExpense(undefined);
        }}
        onShowSnackbar={onShowSnackbar}
        editData={editingExpense}
      />

      <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false })} PaperProps={{ sx: { bgcolor: '#1a1a1a', color: 'white' } }}>
        <DialogTitle>支出を削除</DialogTitle>
        <DialogContent>
          <Typography>
            「{deleteDialog.expense?.category}」の支出({formatCurrency(deleteDialog.expense?.amount || 0)})を削除しますか?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false })} sx={{ color: '#888' }}>
            キャンセル
          </Button>
          <Button onClick={handleDelete} sx={{ color: '#EF4444' }} autoFocus>
            削除
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={showDatePicker} onClose={() => setShowDatePicker(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { bgcolor: '#1a1a1a', color: 'white' } }}>
        <DialogTitle>日付を選択</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <IconButton onClick={prevMonth} sx={{ color: 'white' }}>&lt;</IconButton>
            <Typography fontWeight="bold">{calendarYM.year}年 {calendarYM.month + 1}月</Typography>
            <IconButton onClick={nextMonth} sx={{ color: 'white' }}>&gt;</IconButton>
          </Box>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.5 }}>
            {['日', '月', '火', '水', '木', '金', '土'].map(d => (
              <Typography key={d} textAlign="center" fontSize={14} color="#888" fontWeight="bold">{d}</Typography>
            ))}
            {(() => {
              const firstDay = new Date(calendarYM.year, calendarYM.month, 1).getDay();
              const daysInMonth = new Date(calendarYM.year, calendarYM.month + 1, 0).getDate();
              const days = [];
              for (let i = 0; i < firstDay; i++) days.push(<Box key={`e-${i}`} />);
              for (let d = 1; d <= daysInMonth; d++) {
                const isSelectedDay = isSelected(d);
                const isTodayDay = isToday(d, calendarYM);
                days.push(
                  <Box
                    key={d}
                    onClick={() => selectDate(d)}
                    sx={{
                      p: 1,
                      textAlign: 'center',
                      cursor: 'pointer',
                      borderRadius: '8px',
                      bgcolor: isSelectedDay ? '#EF4444' : isTodayDay ? '#333' : 'transparent',
                      color: isSelectedDay ? 'white' : isTodayDay ? '#EF4444' : 'white',
                      fontWeight: isSelectedDay || isTodayDay ? 'bold' : 'normal',
                      fontSize: 14,
                      '&:hover': { bgcolor: isSelectedDay ? '#dc2626' : '#2a2a2a' }
                    }}
                  >
                    {d}
                  </Box>
                );
              }
              return days;
            })()}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDatePicker(false)} sx={{ color: '#888' }}>キャンセル</Button>
          <Button onClick={confirmDateSelection} sx={{ color: '#EF4444' }}>確定</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={showDateRangePicker} onClose={() => setShowDateRangePicker(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { bgcolor: '#1a1a1a', color: 'white' } }}>
        <DialogTitle>期間を選択</DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2 }}>
            <Typography fontSize={15} color="#888" mb={1}>
              {selectingStart ? '開始日を選択してください' : '終了日を選択してください'}
            </Typography>
            {rangeStart && (
              <Typography fontSize={15} color="#EF4444">
                開始: {rangeStart.toLocaleDateString('ja-JP')}
              </Typography>
            )}
            {rangeEnd && (
              <Typography fontSize={15} color="#EF4444">
                終了: {rangeEnd.toLocaleDateString('ja-JP')}
              </Typography>
            )}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <IconButton onClick={rangePrevMonth} sx={{ color: 'white' }}>&lt;</IconButton>
            <Typography fontWeight="bold">{rangeCalendarYM.year}年 {rangeCalendarYM.month + 1}月</Typography>
            <IconButton onClick={rangeNextMonth} sx={{ color: 'white' }}>&gt;</IconButton>
          </Box>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.5 }}>
            {['日', '月', '火', '水', '木', '金', '土'].map(d => (
              <Typography key={d} textAlign="center" fontSize={14} color="#888" fontWeight="bold">{d}</Typography>
            ))}
            {(() => {
              const firstDay = new Date(rangeCalendarYM.year, rangeCalendarYM.month, 1).getDay();
              const daysInMonth = new Date(rangeCalendarYM.year, rangeCalendarYM.month + 1, 0).getDate();
              const days = [];
              for (let i = 0; i < firstDay; i++) days.push(<Box key={`e-${i}`} />);
              for (let d = 1; d <= daysInMonth; d++) {
                const inRange = isInRange(d);
                const isEnd = isRangeEnd(d);
                const isTodayDay = isToday(d, rangeCalendarYM);
                days.push(
                  <Box
                    key={d}
                    onClick={() => selectRangeDate(d)}
                    sx={{
                      p: 1,
                      textAlign: 'center',
                      cursor: 'pointer',
                      borderRadius: '8px',
                      bgcolor: isEnd ? '#EF4444' : inRange ? '#5a2020' : isTodayDay ? '#333' : 'transparent',
                      color: isEnd || inRange ? 'white' : isTodayDay ? '#EF4444' : 'white',
                      fontWeight: isEnd || isTodayDay ? 'bold' : 'normal',
                      fontSize: 14,
                      '&:hover': { bgcolor: isEnd ? '#dc2626' : inRange ? '#6a2525' : '#2a2a2a' }
                    }}
                  >
                    {d}
                  </Box>
                );
              }
              return days;
            })()}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setShowDateRangePicker(false); setRangeStart(null); setRangeEnd(null); setSelectingStart(true); }} sx={{ color: '#888' }}>キャンセル</Button>
          <Button onClick={confirmRangeSelection} disabled={!rangeStart || !rangeEnd} sx={{ color: '#EF4444' }}>確定</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default ExpenseScreen;
